import React, { useState, useEffect, useRef } from 'react';
import { Activity, AlertCircle, Clock, MessageSquare } from 'lucide-react';
import { getAssignmentProgress } from '../../lib/api';
import { studentLabel } from '../../lib/format';

const POLL_MS = 30000;

/**
 * Live roster status for one assignment (master spec Phase 6 items 1-2).
 * Polls on an interval — no websockets in v1, per spec.
 *
 * The chips are INFORMATIONAL. They answer "who needs me right now" and
 * deliberately do not look like alarms: a stalled student is a teaching
 * opportunity, not an incident.
 */
const LiveDraftBoard = ({ assignmentId }) => {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!assignmentId) return undefined;
    let cancelled = false;

    const load = async () => {
      try {
        const result = await getAssignmentProgress(assignmentId);
        if (!cancelled) {
          setData(result);
          setLoadError(false);
        }
      } catch (e) {
        console.error('Live status load failed:', e);
        // Keep the last good roster on screen rather than blanking it; a
        // flaky poll must not erase what the teacher is reading.
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    timerRef.current = setInterval(load, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timerRef.current);
    };
  }, [assignmentId]);

  if (loading) {
    return (
      <div className="card-surface p-4">
        <p className="text-slate-400 text-sm">Loading live status…</p>
      </div>
    );
  }

  const students = data?.students || [];
  if (!students.length) {
    return (
      <div className="card-surface p-4">
        <h3 className="font-heading text-lg font-semibold text-white mb-1">Live Status</h3>
        <p className="text-slate-400 text-sm">
          No students have opened this assignment yet. Share the student link and
          their progress will appear here.
        </p>
      </div>
    );
  }

  // Who needs me first: flagged, then actively writing, then everyone else.
  const ordered = [...students].sort((a, b) => {
    if ((b.flags?.length || 0) !== (a.flags?.length || 0)) {
      return (b.flags?.length || 0) - (a.flags?.length || 0);
    }
    return (b.is_writing_now ? 1 : 0) - (a.is_writing_now ? 1 : 0);
  });

  return (
    <div className="card-surface p-4" data-testid="live-draftboard">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading text-lg font-semibold text-white">Live Status</h3>
        <div className="flex items-center gap-3 text-xs">
          <span className="flex items-center gap-1 text-teal-flow">
            <Activity size={13} /> {data.writing_now} writing now
          </span>
          <span className="flex items-center gap-1 text-gold-bright">
            <Clock size={13} /> {data.stalled} stalled
          </span>
          <span className="text-slate-400">{data.submitted} submitted</span>
        </div>
      </div>

      {loadError && (
        <p className="text-slate-400 text-xs mb-2">
          Live updates paused — showing the last status we loaded.
        </p>
      )}

      <div className="space-y-2" role="list">
        {ordered.map((s) => (
          <div
            key={s.draft_id}
            role="listitem"
            className="flex items-center gap-3 p-2 rounded-lg bg-slate-900/40"
          >
            <span className="text-white text-sm font-medium w-32 truncate" title={studentLabel(s)}>
              {studentLabel(s)}
            </span>

            {s.is_writing_now && (
              <span className="status-chip status-chip--success flex items-center gap-1">
                <Activity size={11} /> Writing now
              </span>
            )}
            {s.status === 'submitted' && (
              <span className="status-chip status-chip--primary">Submitted</span>
            )}

            <span className="text-slate-400 text-xs">{s.word_count} words</span>
            {s.time_on_task_minutes > 0 && (
              <span className="text-slate-500 text-xs flex items-center gap-1">
                <Clock size={11} /> {s.time_on_task_minutes}m on task
              </span>
            )}
            {s.coach_turns > 0 && (
              <span className="text-slate-500 text-xs flex items-center gap-1">
                <MessageSquare size={11} /> {s.coach_turns} coach
              </span>
            )}

            <div className="flex items-center gap-2 ml-auto">
              {(s.flags || []).map((f) => (
                <span
                  key={f.id}
                  title={f.detail}
                  className="status-chip status-chip--gold flex items-center gap-1"
                >
                  <AlertCircle size={11} /> {f.label}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LiveDraftBoard;
